import { IOgqlV1Column } from '@opengov/ogql-types';
import {
  ColumnDefinition,
  ColumnValue,
  ColumnValueSegment,
  IAccountSummary,
  IColumnSource,
  getRowGroupName,
  isCalculationExpression,
  isMultiHierarchyCalculatedRowGroup
} from '@opengov/reporting-types';
import {
  collect,
  MultiGroupingOverflowError,
  filterZerosInChildRows,
  filterZerosInParentRows,
  mapWithConcurrency,
  shouldSuppressZerosInRows
} from './getRowDataUtils';
import type { LegacyRowSource } from './getRowDataWithCache';

import * as api from '../../api/reportingApi';
import { LocaleText } from '../../locale';
import { generateUniqueRowKey } from '../../utils/uniqueKeyUtils';

export enum DataTableStatus {
  Loading = 'loading',
  Updating = 'updating',
  Current = 'current',
  Error = 'error'
}

export interface QueryContext {
  localeText: LocaleText['multidataset'];
  gridCacheEnabled: boolean;
  entityFiscalYearStartMonth?: number;
  entitySubdomain?: string;
  /**
   * Set only for a combined report, from the loaded report's `entityGroupId`. Report-level, unlike
   * the two entity fields above — which chart of accounts a query reads is a property of the saved
   * report, not of who is viewing it.
   */
  entityGroupId?: string;
  initialize?: (args: { parentRows: any[] }) => void;
  /**
   * Upper bound on simultaneous row requests from one fan-out, the same configured limit the
   * prefetch uses. Unset means unbounded, as before the limit existed.
   */
  maxParallelRequests?: number;
  organizeColumnsByAccountType?: boolean;
  updateTableStatus?: (status: DataTableStatus) => void;
}

export function transformParentRowsToAgGrid(
  parentRows: ColumnValueSegment[][],
  rowGroups: LegacyRowSource[],
  metadataColumnsPerRow: IOgqlV1Column[][],
  suppressZerosInRows: boolean,
  formatting: IAccountSummary['formatting'],
  columnGroups: IColumnSource[],
  columnDefinitions: ColumnDefinition[]
) {
  if (!parentRows) return [];

  const transformedParentRows = rowGroups.map((rowGroup, index) => {
    const canExpand =
      !isCalculationExpression(rowGroup) &&
      !isMultiHierarchyCalculatedRowGroup(rowGroup) &&
      rowGroup?.grouping?.length > 0;

    return {
      id: rowGroup.id,
      rowGroupIndex: index,
      row_group_0: getRowGroupName(rowGroup), // eslint-disable-line camelcase
      values: parentRows[index].slice(1),
      canExpand,
      groupData: {
        type: rowGroup.type,
        metadataColumns: metadataColumnsPerRow[index]
      }
    };
  });

  return suppressZerosInRows
    ? filterZerosInParentRows(transformedParentRows, metadataColumnsPerRow, formatting, columnGroups, columnDefinitions)
    : transformedParentRows;
}

export const transformChildRowsToAgGrid = (
  childRows: ColumnValue[],
  rowGroup: LegacyRowSource,
  rowGroupIndex: number,
  nodeLevel: number,
  metadataColumns: IOgqlV1Column[] = [],
  suppressZerosInRows: boolean,
  formatting: IAccountSummary['formatting'],
  columnGroups: IColumnSource[],
  columnDefinitions: ColumnDefinition[],
  parentRowGroupId?: string,
  parentRowKey?: string
): any[] => {
  if (!childRows) return [];
  const filteredChildRows = suppressZerosInRows
    ? filterZerosInChildRows(childRows, metadataColumns, formatting, columnGroups, columnDefinitions)
    : childRows;

  return filteredChildRows.map((row, index) => {
    const uniqueRowKey = generateUniqueRowKey(parentRowGroupId ?? rowGroup.id, index, row.slice(1));
    return {
      id: `${parentRowGroupId ?? rowGroup.id}.${row[0]}-key-${uniqueRowKey}`,
      rowKey: `${parentRowKey ?? rowGroup.id}.${row[0]}`,
      rowGroupIndex,
      row_group_0: getRowGroupName(rowGroup), // eslint-disable-line camelcase
      [`row_group_${nodeLevel}`]: row[0],
      values: row.slice(1),
      canExpand:
        !isCalculationExpression(rowGroup) &&
        !isMultiHierarchyCalculatedRowGroup(rowGroup) &&
        nodeLevel < rowGroup.grouping.length,
      groupData: {
        type: rowGroup.type,
        metadataColumns
      }
    };
  });
};

export async function getRowData(
  context: QueryContext,
  report: IAccountSummary,
  reportConfigurationId: string,
  columnValues: ColumnValue[],
  columnGroups: IColumnSource[],
  columnDefinitions: ColumnDefinition[]
) {
  if (!columnValues?.length) return [];
  const { rowGroups = [] } = (report ?? {}) as { rowGroups: LegacyRowSource[] };

  const pivotColumnGroupIds = new Set(columnValues.map((col) => col[0]));
  if (report.columnGroups.some((group) => !pivotColumnGroupIds.has(group.id))) return [];

  const suppressZerosInRows = shouldSuppressZerosInRows(report);
  const responses = await mapWithConcurrency(rowGroups, context.maxParallelRequests, (_, rowIndex) =>
    api.pivotRows({
      report,
      rowIndex,
      parentQuery: true,
      columnValues,
      entitySubdomain: context.entitySubdomain,
      entityFiscalYearStartMonth: context.entityFiscalYearStartMonth,
      entityGroupId: context.entityGroupId,
      reportConfigurationId
    })
  );

  const rows = collect<ColumnValueSegment[]>(responses, 'data');
  const metadata = collect<IOgqlV1Column[]>(responses, 'metadata.columns', (s) => [s]);

  const parentRows = transformParentRowsToAgGrid(
    rows,
    rowGroups,
    metadata,
    suppressZerosInRows,
    report.formatting,
    columnGroups,
    columnDefinitions
  );

  context.updateTableStatus?.(DataTableStatus.Current);
  context.initialize?.({ parentRows });

  return parentRows;
}

interface GetChildDataArgs {
  offset: number;
  limit: number;
  groupingValues: string[];
  rowGroupIndex: number;
  nodeLevel: number;
  parentId?: string;
  parentRowKey?: string;
  reportConfigurationId: string;
}

export async function getChildData(
  context: QueryContext,
  report: IAccountSummary,
  columnValues: ColumnValue[],
  payload: GetChildDataArgs,
  columnGroups: IColumnSource[],
  columnDefinitions: ColumnDefinition[]
) {
  const { rowGroupIndex, limit, offset, nodeLevel, parentId, groupingValues, reportConfigurationId, parentRowKey } =
    payload;

  const currentRowGroup = report.rowGroups[rowGroupIndex] as LegacyRowSource;
  const suppressZerosInRows = shouldSuppressZerosInRows(report);

  if (
    currentRowGroup &&
    !isCalculationExpression(currentRowGroup) &&
    !isMultiHierarchyCalculatedRowGroup(currentRowGroup) &&
    (currentRowGroup.grouping.length === 0 || nodeLevel > currentRowGroup.grouping.length)
  ) {
    throw new MultiGroupingOverflowError();
  }

  const pivotRows = await api.pivotRows({
    report,
    rowIndex: rowGroupIndex,
    columnValues,
    limit,
    offset,
    groupingValues,
    entityFiscalYearStartMonth: context.entityFiscalYearStartMonth,
    entitySubdomain: context.entitySubdomain,
    entityGroupId: context.entityGroupId,
    reportConfigurationId
  });

  return {
    childData: transformChildRowsToAgGrid(
      pivotRows?.data,
      currentRowGroup,
      rowGroupIndex,
      nodeLevel,
      pivotRows?.metadata.columns,
      suppressZerosInRows,
      report.formatting,
      columnGroups,
      columnDefinitions,
      parentId,
      parentRowKey
    ),
    returnedPivotRowCount: pivotRows.data.length
  };
}
